// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A visual timeline component for displaying asset events and shipments.
 * This module provides an interactive visualization of an asset's history,
 * including shipments, locations, and key events over time. The timeline
 * supports various interaction modes, responsive layout, and rich tooltips
 * for detailed event information.
 *
 * Key features:
 * - Interactive event visualization
 * - Responsive SVG-based layout
 * - Rich tooltip integration
 * - Automatic overlap detection
 * - Today's date indicator
 * - Configurable time scale
 * - Dynamic width adjustment
 * - Accessibility support
 */
import { Asset, EventGroup } from '@/asset/api/types';
import {
  AssetShipmentTimelineSegment,
  AssetShipmentTimelineSegmentWarning,
} from '@/asset/components/asset-shipment-timeline-segment';
import { AssetShipmentTimelineTooltip } from '@/asset/components/asset-shipment-timeline-tooltip';
import { TimelineAxis, TimelineAxisDensity } from '@/components/timeline-axis';
import { TimelineDateIndicator } from '@/components/timeline-date-indicator';
import { TimelineFocusIndicator } from '@/components/timeline-focus-indicator';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { Box, useTheme } from '@mui/material';
import { localPoint } from '@visx/event';
import { scaleTime } from '@visx/scale';
import { getStringWidth } from '@visx/text';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { DateTime, Interval } from 'luxon';
import { MouseEvent, useRef } from 'react';

/**
 * Unique identifier for shipment segment input fields.
 * Used for form integration and accessibility labeling.
 */
export const SHIPMENT_SEGMENT_INPUT_ID = 'input';

/**
 * Visual constants for timeline layout.
 * These values control the spacing and sizing of timeline elements.
 *
 * - DATE_AXIS_HEIGHT: Height of the date axis in pixels
 * - BAR_PADDING: Vertical padding between timeline bars
 * - BAR_HEIGHT: Height of individual timeline bars
 * - ROW_HEIGHT: Total height of a timeline row including padding
 */
const DATE_AXIS_HEIGHT = 40;
const BAR_PADDING = 4;
const BAR_HEIGHT = 30;
const ROW_HEIGHT = BAR_HEIGHT + BAR_PADDING * 2;

/**
 * Props for configuring the AssetTimeline component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * Asset information required for timeline display.
   * Includes key identifiers and current status.
   * Only requires essential properties to minimize data requirements.
   *
   * @example
   * ```ts
   * const asset = {
   *   key: 'asset-123',
   *   serial: 'SN123456',
   *   currentLocation: { name: 'Warehouse A' },
   *   inventoryDate: DateTime.now()
   * };
   * ```
   */
  asset: Asset;

  /**
   * Ordered collection of event groups representing asset shipments.
   * Each group contains related events that form a timeline segment.
   * Events within each group should be ordered by sequence number.
   *
   * @example
   * ```ts
   * const shipments = [
   *   {
   *     id: 'ship-1',
   *     origin: { name: 'Warehouse A' },
   *     destination: { name: 'Customer Site' },
   *     events: [
   *       {
   *         sequence: 1,
   *         type: { name: 'Picked Up' },
   *         occurredAt: DateTime.now()
   *       }
   *     ]
   *   }
   * ];
   * ```
   */
  shipments: EventGroup[];

  /**
   * The density of tick marks and labels on the timeline.
   * Controls how detailed the time axis appears.
   * Higher density shows more date labels and grid lines.
   *
   * Options:
   * - 'compact': Minimal labels for space efficiency
   * - 'normal': Standard spacing and label frequency
   * - 'comfortable': Maximum detail with all labels
   */
  density: TimelineAxisDensity;

  /**
   * The time interval to display in the timeline.
   * Determines the start and end dates of the visible range.
   * Used to calculate the time scale and axis bounds.
   *
   * @example
   * ```ts
   * const interval = DateTime.now()
   *   .minus({ months: 1 })
   *   .until(DateTime.now().plus({ months: 1 }));
   * ```
   */
  interval: Interval;
}

/**
 * A visual timeline component for displaying asset history.
 * Renders an interactive SVG-based timeline showing shipments,
 * locations, and key events in an asset's lifecycle. Supports
 * tooltips, overlapping event detection, and responsive layout.
 *
 * Features:
 * - Interactive tooltips showing event details
 * - Automatic overlap detection between events
 * - Responsive layout with dynamic width adjustment
 * - Configurable time scale and density
 * - Today's date indicator with custom styling
 * - Origin and destination labels with auto-sizing
 * - Mouse event handling for interactions
 * - Portal-based tooltip rendering
 * - Theme integration for consistent styling
 *
 * Layout Structure:
 * ```
 * ┌────────────────────────────────────┐
 * │            Date Axis               │
 * ├────────────────────────────────────┤
 * │ Origin → [Event Bar 1] → Dest     │
 * │ Origin → [Event Bar 2] → Dest     │
 * │ Origin → [Event Bar 3] → Dest     │
 * └────────────────────────────────────┘
 * ```
 *
 * Visual Elements:
 * - Date axis with configurable density
 * - Today's date indicator line
 * - Event bars with gradient effects
 * - Origin/destination labels
 * - Interactive tooltips
 * - Overlap warning indicators
 *
 * Usage example:
 * ```tsx
 * const asset = {
 *   key: 'asset-123',
 *   serial: 'SN123456',
 *   currentLocation: { name: 'Warehouse A' },
 *   inventoryDate: DateTime.now()
 * };
 *
 * const interval = DateTime.now()
 *   .minus({ months: 1 })
 *   .until(DateTime.now().plus({ months: 1 }));
 *
 * return (
 *   <AssetTimeline
 *     asset={asset}
 *     shipments={shipments}
 *     density="normal"
 *     interval={interval}
 *   />
 * );
 * ```
 *
 * @param props - Component configuration options
 */
export function AssetTimeline(props: Props) {
  const today = DateTime.now().startOf('day');

  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const resizeObserver = useResizeObserver(boxRef);

  const {
    tooltipTop,
    tooltipLeft,
    tooltipOpen,
    tooltipData,
    showTooltip,
    hideTooltip,
  } = useTooltip<EventGroup>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const rowCount = Math.max(3, props.shipments.length);
  const height =
    BAR_HEIGHT * rowCount + BAR_PADDING * 2 * rowCount + DATE_AXIS_HEIGHT;

  const paddingLeft = props.shipments.reduce((padding, segment) => {
    if (segment.origin === null) {
      return padding;
    }

    const width = getStringWidth(
      getEffectiveLocationName(segment.origin),
      theme.typography.caption
    );

    if (width === null) {
      return padding;
    }

    return Math.max(width, padding);
  }, 0);

  const paddingRight = props.shipments.reduce((padding, segment) => {
    if (segment.destination === null) {
      return padding;
    }

    const width = getStringWidth(
      getEffectiveLocationName(segment.destination),
      theme.typography.caption
    );

    if (width === null) {
      return padding;
    }

    return Math.max(width, padding);
  }, 0);

  const dateScale = scaleTime({
    range: [paddingLeft, resizeObserver.width - paddingRight],
    domain: [props.interval.start.toJSDate(), props.interval.end.toJSDate()],
  });

  const getY = (index: number) => {
    const y = index * ROW_HEIGHT + BAR_PADDING;

    return DATE_AXIS_HEIGHT + y;
  };

  const createMouseMoveHandler =
    (eventGroup: EventGroup) => (event: MouseEvent<SVGElement>) => {
      if (svgRef.current === null) {
        return;
      }

      const point = localPoint(svgRef.current, event);

      if (point === null) {
        return;
      }

      showTooltip({
        tooltipTop: point.y,
        tooltipLeft: point.x,
        tooltipData: eventGroup,
      });
    };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  return (
    <Box ref={boxRef}>
      <Box ref={containerRef} height={height} overflow="auto">
        <svg
          ref={svgRef}
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          <TimelineProvider>
            {tooltipOpen && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  padding: 0,
                  zIndex: theme.zIndex.tooltip,
                  color: theme.palette.text.primary,
                }}
              >
                {tooltipData && (
                  <AssetShipmentTimelineTooltip shipment={tooltipData} />
                )}
              </TooltipInPortal>
            )}
            <TimelineAxis
              scale={dateScale}
              width={resizeObserver.width}
              density={props.density}
              interval={props.interval}
              axisHeight={DATE_AXIS_HEIGHT}
              chartHeight={height}
            />
            <TimelineDateIndicator
              y={DATE_AXIS_HEIGHT}
              height={height}
              scale={dateScale}
              dateTime={today}
              color={theme.palette.primary.light}
            />
            {props.shipments.map((segment, index, segments) => {
              const y = getY(index);

              const isInvalid = segments.some((other) => {
                if (segment.id === other.id) {
                  return false;
                }

                if (
                  segment.minimumDate === null ||
                  segment.maximumDate === null
                ) {
                  return false;
                }

                if (other.minimumDate === null || other.maximumDate === null) {
                  return false;
                }

                const segmentInterval = segment.minimumDate.until(
                  segment.maximumDate
                );

                const otherInterval = other.minimumDate.until(
                  other.maximumDate
                );

                return segmentInterval.overlaps(otherInterval);
              });

              const previousSegment = segments.reduce<EventGroup | null>(
                (previous, other) => {
                  if (segment.id === other.id) {
                    return previous;
                  }

                  if (
                    segment.minimumDate === null ||
                    other.maximumDate === null
                  ) {
                    return previous;
                  }

                  if (other.maximumDate > segment.minimumDate) {
                    return previous;
                  }

                  if (previous === null || previous.maximumDate === null) {
                    return other;
                  }

                  return previous.maximumDate > other.maximumDate
                    ? previous
                    : other;
                },
                null
              );

              const nextSegment = segments.reduce<EventGroup | null>(
                (next, other) => {
                  if (segment.id === other.id) {
                    return next;
                  }

                  if (
                    segment.maximumDate === null ||
                    other.minimumDate === null
                  ) {
                    return next;
                  }

                  if (other.minimumDate < segment.maximumDate) {
                    return next;
                  }

                  if (next === null || next.minimumDate === null) {
                    return other;
                  }

                  return next.minimumDate < other.minimumDate ? next : other;
                },
                null
              );

              const previousDestination = previousSegment?.destination ?? null;

              const nextOrigin = nextSegment?.origin ?? null;

              const warnings: AssetShipmentTimelineSegmentWarning[] = [];

              if (
                segment.origin !== null &&
                previousDestination !== null &&
                previousDestination.id !== segment.origin.id
              ) {
                warnings.push('origin');
              }

              if (
                segment.destination !== null &&
                nextOrigin !== null &&
                nextOrigin.id !== segment.destination.id
              ) {
                warnings.push('destination');
              }

              const rawMaximum = nextSegment?.minimumDate ?? props.interval.end;
              const maximumDateTime =
                rawMaximum > props.interval.end
                  ? props.interval.end
                  : rawMaximum;

              const rawMinimum =
                previousSegment?.maximumDate ?? props.interval.start;
              const minimumDateTime =
                rawMinimum < props.interval.start
                  ? props.interval.start
                  : rawMinimum;

              const focusY = y - BAR_PADDING;
              const focusHeight = BAR_HEIGHT + BAR_PADDING * 2;

              return (
                <g key={segment.id}>
                  {segment.id === SHIPMENT_SEGMENT_INPUT_ID && (
                    <TimelineFocusIndicator
                      x={0}
                      y={focusY}
                      width={resizeObserver.width}
                      height={focusHeight}
                    />
                  )}
                  <AssetShipmentTimelineSegment
                    y={y}
                    scale={dateScale}
                    asset={props.asset}
                    height={BAR_HEIGHT}
                    shipment={segment}
                    warnings={warnings}
                    isInvalid={isInvalid}
                    minimumDateTime={minimumDateTime}
                    maximumDateTime={maximumDateTime}
                    onMouseMove={createMouseMoveHandler(segment)}
                    onMouseLeave={handleMouseLeave}
                  />
                </g>
              );
            })}
          </TimelineProvider>
        </svg>
      </Box>
    </Box>
  );
}
